import Constants from 'expo-constants'
import { Children, forwardRef, isValidElement, useMemo, useState } from 'react'
import { Platform, View } from 'react-native'
import { Modalize } from 'react-native-modalize'
import type { IHandles } from 'react-native-modalize/lib/options'
import { isDEV, logger } from '~/utils'
import * as Comp from './bottom-sheets.styles'
import {
  BottomSheetsBackButton,
  BottomSheetsContainer,
  BottomSheetsDescription,
  BottomSheetsHeader,
  BottomSheetsRightButton,
  BottomSheetsTitle,
} from './components'
import type { IBottomSheetsProps } from './types'
import { SCOPE_WARNING_MESSAGE } from './utils/const'

const DEFAULT_MARGIN_BOTTOM = 12

const TOP_OFFSET =
  Platform.OS === 'ios'
    ? Constants.statusBarHeight * 2.3
    : Constants.statusBarHeight

const BottomSheetsRoot = forwardRef<IHandles, IBottomSheetsProps>(
  ({ children, testID = 'inputTest', expandable, noHeader, ...props }, ref) => {
    const [layoutHeight, setLayoutHeight] = useState(266)

    const renderInternalComponents = Children.map(children, child => {
      if (!isValidElement(child)) return

      if (child.type === BottomSheetsHeader && !noHeader) return child

      if (child.type === BottomSheetsContainer) return child

      if (isDEV()) {
        logger.warning(SCOPE_WARNING_MESSAGE)
        return
      }
    })

    const expandableProps = useMemo(
      () => ({
        HeaderComponent: (
          <View
            style={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              paddingVertical: 8,
            }}
          >
            <View
              style={{
                width: 32,
                height: 4,
                borderRadius: 8,
                backgroundColor: '#E6E6E6',
              }}
            />
          </View>
        ),
        snapPoint: layoutHeight + DEFAULT_MARGIN_BOTTOM,
      }),
      [layoutHeight]
    )

    const notExpandableProps = { adjustToContentHeight: true }

    const customProps =
      expandable || noHeader ? expandableProps : notExpandableProps

    return (
      <Modalize
        ref={ref}
        withHandle={false}
        modalTopOffset={TOP_OFFSET}
        {...customProps}
      >
        <Comp.Root
          {...props}
          testID={testID}
          onLayout={event => {
            if (event?.nativeEvent)
              setLayoutHeight(
                event.nativeEvent.layout.height + DEFAULT_MARGIN_BOTTOM
              )
          }}
        >
          {renderInternalComponents}
        </Comp.Root>
      </Modalize>
    )
  }
)

export const BottomSheets = {
  Root: BottomSheetsRoot,
  Container: BottomSheetsContainer,
  Description: BottomSheetsDescription,
  BackButton: BottomSheetsBackButton,
  RightButton: BottomSheetsRightButton,
  Header: BottomSheetsHeader,
  Title: BottomSheetsTitle,
}

export type { IBottomSheetsProps }
